import type { RatingApi } from "../../api/RatingApi";
import type { ApClassTypeFetchOptions } from "../../generated/dto/ApClassTypeFetchOptions";
import type { ApClassTypeStaff } from "../../generated/dto/ApClassTypeStaff";
import type { CreateRating } from "../../generated/dto/CreateRating";
import type { CreateRatingProgram } from "../../generated/dto/CreateRatingProgram";
import type { DeleteRatingProgramStaff } from "../../generated/dto/DeleteRatingProgramStaff";
import type { DeleteRatingStaff } from "../../generated/dto/DeleteRatingStaff";
import type { JpClassTypeFetchOptions } from "../../generated/dto/JpClassTypeFetchOptions";
import type { JpClassTypeStaff } from "../../generated/dto/JpClassTypeStaff";
import type { PersonFetchOptions } from "../../generated/dto/PersonFetchOptions";
import type { PersonRatingFetchOptions } from "../../generated/dto/PersonRatingFetchOptions";
import type { PersonRatingGrid } from "../../generated/dto/PersonRatingGrid";
import type { PersonRatingSearchParams } from "../../generated/dto/PersonRatingSearchParams";
import type { PersonRatingStaff } from "../../generated/dto/PersonRatingStaff";
import type { PersonRatingStaffSearchResult } from "../../generated/dto/PersonRatingStaffSearchResult";
import type { PersonRef } from "../../generated/dto/PersonRef";
import type { PersonStaff } from "../../generated/dto/PersonStaff";
import type { ProgramTypeStaff } from "../../generated/dto/ProgramTypeStaff";
import type { RatingFetchOptions } from "../../generated/dto/RatingFetchOptions";
import type { RatingProgramFetchOptions } from "../../generated/dto/RatingProgramFetchOptions";
import type { RatingProgramStaff } from "../../generated/dto/RatingProgramStaff";
import type { RatingSearchParams } from "../../generated/dto/RatingSearchParams";
import type { RatingStaff } from "../../generated/dto/RatingStaff";
import type { RatingStaffSearchResult } from "../../generated/dto/RatingStaffSearchResult";
import type { UpdateRating } from "../../generated/dto/UpdateRating";
import type { UpdateRatingProgram } from "../../generated/dto/UpdateRatingProgram";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class RatingFrontendService {
  constructor(private readonly api: RatingApi) {}
  private readonly ratingStaffCache: Map<number, RatingStaff> = new Map();
  private readonly ratingProgramStaffCache: Map<number, RatingProgramStaff> = new Map();
  async getRatingStaff(id: number, options?: RatingFetchOptions): Promise<RatingStaff> {
    const __cached = this.ratingStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getRatingStaff(id, options);
    this.ratingStaffCache.set(id, __value);
    return __value
  }
  async getRatingProgramStaff(id: number, options?: RatingProgramFetchOptions): Promise<RatingProgramStaff> {
    const __cached = this.ratingProgramStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getRatingProgramStaff(id, options);
    this.ratingProgramStaffCache.set(id, __value);
    return __value
  }
  async getPersonRatingStaff(id: number, options?: PersonRatingFetchOptions): Promise<PersonRatingStaff> {
    return this.api.getPersonRatingStaff(id, options)
  }
  async getAllRatingStaffs(options?: RatingFetchOptions): Promise<Array<RatingStaff>> {
    return this.api.getAllRatingStaffs(options)
  }
  async getAllRatingProgramStaffs(options?: RatingProgramFetchOptions): Promise<Array<RatingProgramStaff>> {
    return this.api.getAllRatingProgramStaffs(options)
  }
  async createRatingStaff(source: CreateRating): Promise<RatingStaff> {
    const __result = await this.api.createRatingStaff(source);
    this.ratingStaffCache.clear();
    return __result
  }
  async createRatingProgramStaff(source: CreateRatingProgram): Promise<RatingProgramStaff> {
    const __result = await this.api.createRatingProgramStaff(source);
    this.ratingProgramStaffCache.clear();
    return __result
  }
  async updateRatingStaff(source: UpdateRating): Promise<RatingStaff> {
    const __result = await this.api.updateRatingStaff(source);
    this.ratingStaffCache.clear();
    return __result
  }
  async updateRatingProgramStaff(source: UpdateRatingProgram): Promise<RatingProgramStaff> {
    const __result = await this.api.updateRatingProgramStaff(source);
    this.ratingProgramStaffCache.clear();
    return __result
  }
  async deleteRatingStaff(source: DeleteRatingStaff): Promise<void> {
    const __result = await this.api.deleteRatingStaff(source);
    this.ratingStaffCache.clear();
    return __result
  }
  async deleteRatingProgramStaff(source: DeleteRatingProgramStaff): Promise<void> {
    const __result = await this.api.deleteRatingProgramStaff(source);
    this.ratingProgramStaffCache.clear();
    return __result
  }
  async searchRatingStaff(params: RatingSearchParams, page: number, perPage: number): Promise<PaginationResult<RatingStaffSearchResult>> {
    return this.api.searchRatingStaff(params, page, perPage)
  }
  async searchPersonRatingStaff(params: PersonRatingSearchParams, page: number, perPage: number): Promise<PaginationResult<PersonRatingStaffSearchResult>> {
    return this.api.searchPersonRatingStaff(params, page, perPage)
  }
  async searchRatingStaffFull(params: RatingSearchParams, page: number, perPage: number, options?: RatingFetchOptions): Promise<PaginationResult<RatingStaff>> {
    return this.api.searchRatingStaffFull(params, page, perPage, options)
  }
  async searchPersonRatingStaffFull(params: PersonRatingSearchParams, page: number, perPage: number, options?: PersonRatingFetchOptions): Promise<PaginationResult<PersonRatingStaff>> {
    return this.api.searchPersonRatingStaffFull(params, page, perPage, options)
  }
  async getRatingStaffOverriddenBy(id: number, options?: RatingFetchOptions): Promise<RatingStaff | null> {
    return this.api.getRatingStaffOverriddenBy(id, options)
  }
  async getRatingStaffTypesRequiringThisRatingAsPrereq(id: number, options?: ApClassTypeFetchOptions): Promise<Array<ApClassTypeStaff>> {
    return this.api.getRatingStaffTypesRequiringThisRatingAsPrereq(id, options)
  }
  async getRatingStaffTypesRequiringThisRatingAsOverkill(id: number, options?: ApClassTypeFetchOptions): Promise<Array<ApClassTypeStaff>> {
    return this.api.getRatingStaffTypesRequiringThisRatingAsOverkill(id, options)
  }
  async getRatingStaffTypesTeachableWithThisRating(id: number, options?: ApClassTypeFetchOptions): Promise<Array<ApClassTypeStaff>> {
    return this.api.getRatingStaffTypesTeachableWithThisRating(id, options)
  }
  async getRatingStaffJpTypesRequiringThisRatingAsPrereq(id: number, options?: JpClassTypeFetchOptions): Promise<Array<JpClassTypeStaff>> {
    return this.api.getRatingStaffJpTypesRequiringThisRatingAsPrereq(id, options)
  }
  async getRatingStaffJpTypesRequiringThisRatingAsOverkill(id: number, options?: JpClassTypeFetchOptions): Promise<Array<JpClassTypeStaff>> {
    return this.api.getRatingStaffJpTypesRequiringThisRatingAsOverkill(id, options)
  }
  async getRatingStaffJpTypesTeachableWithThisRating(id: number, options?: JpClassTypeFetchOptions): Promise<Array<JpClassTypeStaff>> {
    return this.api.getRatingStaffJpTypesTeachableWithThisRating(id, options)
  }
  async getRatingProgramStaffRatingId(id: number, options?: RatingFetchOptions): Promise<RatingStaff | null> {
    return this.api.getRatingProgramStaffRatingId(id, options)
  }
  async getRatingProgramStaffProgramId(id: number): Promise<ProgramTypeStaff | null> {
    return this.api.getRatingProgramStaffProgramId(id)
  }
  async getPersonRatingStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff> {
    return this.api.getPersonRatingStaffPersonId(id, options)
  }
  async getPersonRatingStaffRatingId(id: number, options?: RatingFetchOptions): Promise<RatingStaff> {
    return this.api.getPersonRatingStaffRatingId(id, options)
  }
  async getPersonRatingStaffProgramId(id: number): Promise<ProgramTypeStaff | null> {
    return this.api.getPersonRatingStaffProgramId(id)
  }
  async getPersonRatingGridStaff(person: PersonRef): Promise<PersonRatingGrid> {
    return this.api.getPersonRatingGridStaff(person)
  }
  async getPersonRatingGridMember(person: PersonRef): Promise<PersonRatingGrid> {
    return this.api.getPersonRatingGridMember(person)
  }
}
